import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Power, AlertCircle, CheckCircle2, Clock, X, Check, Timer, Users } from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNotifications } from '../../hooks/useNotifications';
import Tooltip from '../common/Tooltip';

/* ── helper : temps écoulé ────────────────────── */
const timeSince = (ts) => {
  if (!ts) return null;
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return null;
    const min = Math.floor((Date.now() - d.getTime()) / 60000);
    if (min < 0) return "À l'instant";
    if (min < 1) return "À l'instant";
    if (min < 60) return `Il y a ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `Il y a ${h}h`;
    return `Il y a ${Math.floor(h / 24)}j`;
  } catch { return null; }
};

/* ── helper : afficher heure prolongée ───────── */
const formatExtendUntil = (ts) => {
  if (!ts) return null;
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return null;
    if (d.getTime() <= Date.now()) return null;
    const h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    return `Ouvert jusqu'à ${h}h${m}`;
  } catch { return null; }
};

/* ── helper : compte à rebours ───────────────── */
const formatCountdown = (ts) => {
  if (!ts) return null;
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return null;
    const diff = d.getTime() - Date.now();
    if (diff <= 0) return null;
    const totalMin = Math.ceil(diff / 60000);
    if (totalMin < 60) return `${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  } catch { return null; }
};

/* ── config visuelle par statut ───────────────── */
const STATUS_CONFIG = {
  open: {
    gradient: 'linear-gradient(160deg, #1b2a6b 0%, #2d3f8f 45%, #00c9a7 100%)',
    shadowColor: 'rgba(0,201,167,.40)',
    labelAction: 'Appuyez pour fermer',
    labelStatus: 'Ouvert',
    statusColor: '#00c9a7',
  },
  closed: {
    gradient: 'linear-gradient(160deg, #991b1b 0%, #dc2626 50%, #ef4444 100%)',
    shadowColor: 'rgba(239,68,68,.40)',
    labelAction: 'Appuyez pour ouvrir',
    labelStatus: 'Fermé',
    statusColor: '#ef4444',
  },
  uncertain: {
    gradient: 'linear-gradient(160deg, #92400e 0%, #d97706 50%, #f59e0b 100%)',
    shadowColor: 'rgba(245,158,11,.40)',
    labelAction: 'Confirmez le statut',
    labelStatus: 'Incertain',
    statusColor: '#f59e0b',
  },
  be_back: {
    gradient: 'linear-gradient(160deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%)',
    shadowColor: 'rgba(59,130,246,.40)',
    labelAction: 'Appuyez pour ouvrir',
    labelStatus: 'De retour bientôt',
    statusColor: '#3b82f6',
  },
};

/* ── Options de prolongation ──────────────────── */
const EXTEND_OPTIONS = [
  { label: '30 min', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '1h30', minutes: 90 },
  { label: '2h', minutes: 120 },
  { label: '4h', minutes: 240 },
  { label: 'Personnalisé', minutes: null },
];

/* ── Options "Je reviens dans…" ───────────────── */
const RETURN_OPTIONS = [
  { label: '5 min', minutes: 5 },
  { label: '10 min', minutes: 10 },
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: 'Personnalisé', minutes: null },
];

/* ── Modal prolongation ───────────────────────── */
/* ── Modal générique durée — utilisé par Extend ET BeBack ─────── */
const DurationModal = ({
  onClose, onConfirm,
  title, subtitle,
  icon: HeaderIcon, iconColor,
  options, maxMinutes,
  confirmLabel, confirmGradient,
}) => {
  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState('');

  const canConfirm = selected && (selected.minutes !== null || parseInt(custom) > 0);

  const handleConfirm = () => {
    const minutes = selected?.minutes ?? parseInt(custom);
    if (!minutes || minutes < 1) return;
    onConfirm(minutes);
  };

  const content = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.52)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.25rem',
        animation: 'dmFadeIn 0.16s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--md-surface)',
          borderRadius: 'var(--md-radius-xl)',
          padding: '1.25rem',
          width: '100%', maxWidth: 340,
          boxShadow: '0 12px 40px rgba(0,0,0,0.28)',
          animation: 'dmPopIn 0.20s cubic-bezier(0.34,1.4,0.64,1)',
        }}
      >
        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '1rem' }}>
          <div style={{
            width: 38, height: 38, borderRadius: 'var(--md-radius-md)',
            background: `${iconColor}18`, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <HeaderIcon size={19} color={iconColor} strokeWidth={2} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--md-text-primary)', lineHeight: 1.2 }}>
              {title}
            </div>
            <div style={{ fontSize: '0.71rem', color: 'var(--md-text-secondary)', marginTop: '0.15rem', lineHeight: 1.4 }}>
              {subtitle}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'var(--md-surface-2)', border: 'none',
              borderRadius: '50%', width: 28, height: 28,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--md-text-secondary)', flexShrink: 0,
            }}
          >
            <X size={13} />
          </button>
        </div>

        {/* ── Grille options ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '0.85rem' }}>
          {options.map((opt) => {
            const active = selected?.label === opt.label;
            return (
              <button
                key={opt.label}
                onClick={() => setSelected(opt)}
                style={{
                  padding: '0.7rem 0.4rem',
                  borderRadius: 'var(--md-radius-lg)',
                  border: `2px solid ${active ? iconColor : 'var(--md-outline)'}`,
                  background: active ? `${iconColor}15` : 'var(--md-surface-2)',
                  color: active ? iconColor : 'var(--md-text-primary)',
                  fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                  fontFamily: 'inherit', transition: 'all 0.12s',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem',
                }}
              >
                <HeaderIcon size={13} color={active ? iconColor : 'var(--md-text-secondary)'} strokeWidth={2} />
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* ── Champ personnalisé ── */}
        {selected?.minutes === null && (
          <div style={{ marginBottom: '0.85rem' }}>
            <input
              type="number" min="1" max={maxMinutes}
              value={custom}
              onChange={e => setCustom(e.target.value)}
              placeholder="Durée en minutes…"
              className="form-input"
              style={{ textAlign: 'center', fontWeight: 700, fontSize: '1rem' }}
              autoFocus
            />
          </div>
        )}

        {/* ── Bouton confirmer ── */}
        <button
          onClick={handleConfirm}
          disabled={!canConfirm}
          style={{
            width: '100%', padding: '0.8rem',
            borderRadius: 'var(--md-radius-lg)', border: 'none',
            background: canConfirm ? confirmGradient : 'var(--md-outline)',
            color: '#fff', fontWeight: 700, fontSize: '0.9rem',
            cursor: canConfirm ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit', transition: 'all 0.18s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
            boxShadow: canConfirm ? `0 4px 16px ${iconColor}40` : 'none',
          }}
        >
          <Check size={16} />
          {confirmLabel}
        </button>
      </div>

      <style>{`
        @keyframes dmFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes dmPopIn  { from{opacity:0;transform:scale(.93)} to{opacity:1;transform:scale(1)} }
      `}</style>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

/* ── Modal prolongation ───────────────────────── */
const ExtendModal = ({ onClose, onConfirm }) => (
  <DurationModal
    onClose={onClose}
    onConfirm={onConfirm}
    title="Prolonger l'ouverture"
    subtitle="Le magasin restera ouvert pendant encore :"
    icon={Clock}
    iconColor="#00c9a7"
    options={EXTEND_OPTIONS}
    maxMinutes={480}
    confirmLabel="Confirmer la prolongation"
    confirmGradient="linear-gradient(135deg, #009e85, #00c9a7)"
  />
);

/* ── Modal "Je reviens dans…" ─────────────────── */
const BeBackModal = ({ onClose, onConfirm }) => (
  <DurationModal
    onClose={onClose}
    onConfirm={onConfirm}
    title="Je reviens dans…"
    subtitle="Un badge bleu avec compte à rebours sera affiché à vos clients."
    icon={Timer}
    iconColor="#3b82f6"
    options={RETURN_OPTIONS}
    maxMinutes={240}
    confirmLabel='Activer "De retour bientôt"'
    confirmGradient="linear-gradient(135deg, #2563eb, #3b82f6)"
  />
);

/* ════════════════════════════════════════════════ */
const StatusToggle = ({ shop, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(shop?.updatedAt);
  const [pressed, setPressed] = useState(false);
  const [justDone, setJustDone] = useState(false);
  const [showExtend, setShowExtend] = useState(false);
  const [showBeBack, setShowBeBack] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const { sendNotification } = useNotifications();

  /* Compteur clients en attente — lu en direct depuis le shop */
  const waitingCount = shop?.waitingCount || 0;

  useEffect(() => { setLastUpdate(shop?.updatedAt); }, [shop?.updatedAt]);

  /* ── Notif push au commerçant quand un client attend ── */
  useEffect(() => {
    if (shop?.status !== 'be_back' || !waitingCount || waitingCount < 1) return;
    sendNotification(
      `👥 ${waitingCount} client${waitingCount > 1 ? 's' : ''} vous attend${waitingCount > 1 ? 'ent' : ''}`,
      { body: 'Des clients sont sur place. Revenez dès que possible !' }
    );
    // On ne notifie que quand le nombre change (dep sur waitingCount)
  }, [waitingCount]); // eslint-disable-line

  /* ── Compte à rebours live ────────────────────── */
  useEffect(() => {
    if (shop?.status !== 'be_back' || !shop?.returnAt) {
      setCountdown(null);
      return;
    }
    const tick = () => setCountdown(formatCountdown(shop.returnAt));
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [shop?.status, shop?.returnAt]);

  /* ── Toggle ouvert / fermé ────────────────────── */
  const toggleStatus = useCallback(async () => {
    if (!shop || loading) return;
    // Si "be_back", le bouton ouvre directement
    if (shop.status === 'be_back') {
      setLoading(true);
      setPressed(true);
      setTimeout(() => setPressed(false), 150);
      try {
        await updateDoc(doc(db, 'shops', shop.id), {
          status: 'open',
          updatedAt: serverTimestamp(),
          updatedBy: 'merchant',
          returnAt: null,
          waitingCount: 0,   // reset compteur
        });
        sendNotification('Magasin ouvert', { body: 'Votre magasin est maintenant ouvert.' });
        onStatusChange?.('open');
        setLastUpdate(new Date().toISOString());
        setJustDone(true);
        setTimeout(() => setJustDone(false), 1800);
      } catch (err) {
        console.error('StatusToggle error:', err);
        alert('Impossible de mettre à jour le statut. Vérifiez votre connexion.');
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setPressed(true);
    setTimeout(() => setPressed(false), 150);

    /* Bloquer si l'admin a verrouillé le statut */
    if (shop.adminLockedStatus) return;

    const newStatus = shop.status === 'open' ? 'closed' : 'open';
    try {
      await updateDoc(doc(db, 'shops', shop.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: 'merchant',
        extendUntil: null,
        returnAt: null,
      });
      sendNotification(
        `Magasin ${newStatus === 'open' ? 'ouvert' : 'fermé'}`,
        { body: `Votre magasin est maintenant ${newStatus === 'open' ? 'ouvert' : 'fermé'}.` }
      );
      onStatusChange?.(newStatus);
      setLastUpdate(new Date().toISOString());
      setJustDone(true);
      setTimeout(() => setJustDone(false), 1800);
    } catch (err) {
      console.error('StatusToggle error:', err);
      alert('Impossible de mettre à jour le statut. Vérifiez votre connexion.');
    } finally {
      setLoading(false);
    }
  }, [shop, loading, sendNotification, onStatusChange]);

  /* ── Prolonger l'ouverture ────────────────────── */
  const handleExtend = useCallback(async (minutes) => {
    if (!shop) return;
    setShowExtend(false);
    const extendUntil = new Date(Date.now() + minutes * 60 * 1000);
    try {
      await updateDoc(doc(db, 'shops', shop.id), {
        extendUntil: extendUntil.toISOString(),
        updatedAt: serverTimestamp(),
        updatedBy: 'merchant',
      });
      sendNotification(
        '⏰ Ouverture prolongée',
        { body: `Votre magasin restera ouvert jusqu'à ${extendUntil.getHours()}h${String(extendUntil.getMinutes()).padStart(2, '0')}.` }
      );
    } catch (err) {
      console.error('Extend error:', err);
    }
  }, [shop, sendNotification]);

  /* ── "Je reviens dans X minutes" ─────────────── */
  const handleBeBack = useCallback(async (minutes) => {
    if (!shop) return;
    setShowBeBack(false);
    const returnAt = new Date(Date.now() + minutes * 60 * 1000);
    try {
      await updateDoc(doc(db, 'shops', shop.id), {
        status: 'be_back',
        returnAt: returnAt.toISOString(),
        updatedAt: serverTimestamp(),
        updatedBy: 'merchant',
      });
      sendNotification(
        '🔵 Absence temporaire signalée',
        { body: `Vos clients savent que vous revenez dans ${minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h`}.` }
      );
      onStatusChange?.('be_back');
      setLastUpdate(new Date().toISOString());
    } catch (err) {
      console.error('BeBack error:', err);
      alert('Impossible de mettre à jour le statut. Vérifiez votre connexion.');
    }
  }, [shop, sendNotification, onStatusChange]);

  if (!shop) return null;

  const status = shop.status || 'closed';
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.closed;
  const isOpen = status === 'open';
  const adminLocked = shop.adminLockedStatus === true;
  const isUncert = status === 'uncertain';
  const isBeBack = status === 'be_back';
  const isClosed = status === 'closed';
  const timeLabel = timeSince(lastUpdate);
  const extendLabel = isOpen ? formatExtendUntil(shop.extendUntil) : null;

  return (
    <div style={{ padding: '0 1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

      {/* Modal prolongation */}
      {showExtend && (
        <ExtendModal onClose={() => setShowExtend(false)} onConfirm={handleExtend} />
      )}

      {/* Modal "Je reviens dans…" */}
      {showBeBack && (
        <BeBackModal onClose={() => setShowBeBack(false)} onConfirm={handleBeBack} />
      )}

      {/* Alerte statut incertain */}
      {isUncert && (
        <div className="alert alert-warning" style={{ borderRadius: 'var(--md-radius-lg)', padding: '0.9rem 1rem' }}>
          <AlertCircle size={20} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong style={{ display: 'block', marginBottom: '0.15rem', fontSize: '0.9rem' }}>
              Statut incertain !
            </strong>
            <span style={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
              Vous avez quitté le magasin sans fermer. Confirmez votre statut ci-dessous.
            </span>
          </div>
        </div>
      )}

      {/* Alerte "be_back" active ── info pour le commerçant */}
      {isBeBack && (
        <div style={{
          borderRadius: 'var(--md-radius-lg)', padding: '0.9rem 1rem',
          background: 'rgba(59,130,246,0.10)',
          border: '1.5px solid rgba(59,130,246,0.30)',
          display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
        }}>
          <Timer size={20} color="#3b82f6" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong style={{ display: 'block', marginBottom: '0.15rem', fontSize: '0.9rem', color: '#2563eb' }}>
              Badge "De retour bientôt" actif
            </strong>
            <span style={{ fontSize: '0.8rem', lineHeight: 1.45, color: 'var(--md-text-secondary)' }}>
              {countdown
                ? `Vos clients voient un compte à rebours : retour dans ${countdown}.`
                : 'Le délai est écoulé — votre badge est maintenant affiché comme "incertain". Confirmez votre statut.'}
            </span>
          </div>
        </div>
      )}

      {/* Carte toggle principale */}
      <div style={{
        borderRadius: 'var(--md-radius-xl)',
        overflow: 'hidden',
        boxShadow: `0 8px 32px ${cfg.shadowColor}`,
        border: '1px solid var(--md-outline)',
        transition: 'box-shadow 0.3s ease',
      }}>
        {/* Badge verrouillage admin */}
        {adminLocked && (
          <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--r-lg)', padding: '0.55rem 0.85rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem', color: '#ef4444', fontWeight: 700 }}>
            🔒 Statut verrouillé par l'administration
          </div>
        )}

        {/* Bouton principal */}
        <button
          onClick={toggleStatus}
          disabled={loading || adminLocked}
          aria-label={isOpen ? 'Fermer le magasin' : 'Ouvrir le magasin'}
          style={{
            width: '100%', minHeight: 170, border: 'none',
            cursor: (loading || adminLocked) ? 'not-allowed' : 'pointer',
            opacity: adminLocked ? 0.6 : loading ? 0.85 : 1,
            background: cfg.gradient, color: '#fff',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: '0.5rem', fontFamily: 'inherit',
            padding: '1.5rem 1rem', position: 'relative',
            overflow: 'hidden', transition: 'opacity 0.2s, transform 0.15s',
            transform: pressed ? 'scale(0.975)' : 'scale(1)',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {/* Halo */}
          <div style={{
            position: 'absolute', width: 140, height: 140,
            borderRadius: '50%', background: 'rgba(255,255,255,.08)',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -60%)', pointerEvents: 'none',
          }} />

          {/* Icône */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(255,255,255,.2)',
            border: '2px solid rgba(255,255,255,.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s', flexShrink: 0,
          }}>
            {loading ? (
              <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            ) : justDone ? (
              <CheckCircle2 size={36} strokeWidth={2.5} />
            ) : isBeBack ? (
              <Timer size={36} strokeWidth={2} />
            ) : (
              <Power size={36} strokeWidth={2} />
            )}
          </div>

          {/* Label */}
          <span style={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase', textShadow: '0 1px 4px rgba(0,0,0,.25)' }}>
            {loading ? 'Mise à jour…' : justDone ? 'Mis à jour !' : cfg.labelAction}
          </span>

          {/* Badge compte à rebours (be_back) */}
          {isBeBack && countdown && (
            <span style={{
              fontSize: '0.8rem', fontWeight: 700,
              background: 'rgba(255,255,255,0.22)',
              borderRadius: '999px', padding: '0.3rem 0.9rem',
              border: '1px solid rgba(255,255,255,0.35)',
              display: 'flex', alignItems: 'center', gap: '0.35rem',
            }}>
              <Clock size={13} />
              Retour dans {countdown}
            </span>
          )}

          {/* Badge prolongation */}
          {extendLabel && (
            <span style={{
              fontSize: '0.75rem', fontWeight: 600,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '999px', padding: '0.25rem 0.75rem',
              border: '1px solid rgba(255,255,255,0.3)',
            }}>
              {extendLabel}
            </span>
          )}
        </button>

        {/* Barre de statut */}
        <div style={{
          background: 'var(--md-surface)', padding: '0.7rem 1.25rem',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: '0.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--md-text-secondary)' }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
              background: cfg.statusColor,
              animation: (isOpen || isUncert || isBeBack) ? 'statusPulse 2.2s ease-in-out infinite' : 'none',
              display: 'inline-block',
            }} />
            Statut :{' '}
            <strong style={{ color: cfg.statusColor }}>{cfg.labelStatus}</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Compteur clients en attente — affiché seulement en be_back */}
            {isBeBack && waitingCount > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                fontSize: '0.72rem', fontWeight: 700,
                color: '#2563eb',
                background: 'rgba(59,130,246,0.12)',
                borderRadius: '999px',
                padding: '0.2rem 0.6rem',
                border: '1px solid rgba(59,130,246,0.25)',
                animation: 'statusPulse 2.2s ease-in-out infinite',
              }}>
                <Users size={11} strokeWidth={2.5} />
                {waitingCount} en attente
              </span>
            )}
            {timeLabel && (
              <span style={{ fontSize: '0.72rem', color: 'var(--md-text-disabled)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {timeLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Bouton prolonger (seulement si ouvert) */}
      {isOpen && (
        <Tooltip
          text="Vous fermez plus tard que prévu ? Prolongez l'ouverture pour que vos clients le sachent. Le statut restera 'Ouvert' pendant la durée choisie, même après votre heure de fermeture habituelle."
          position="top"
        >
          <button
            onClick={() => setShowExtend(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '0.75rem', borderRadius: 'var(--md-radius-lg)',
              border: '2px solid var(--md-outline)',
              background: 'var(--md-surface-2)',
              color: 'var(--md-text-primary)',
              fontWeight: 700, fontSize: '0.88rem',
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'all 0.2s', width: '100%',
            }}
          >
            <Clock size={16} />
            Prolonger l'ouverture
          </button>
        </Tooltip>
      )}

      {/* ── Bouton "Je reviens dans…" (seulement si fermé ou be_back) ── */}
      {(isClosed || isBeBack) && (
        <Tooltip
          text="Partez brièvement ? Signalez votre retour pour que vos clients puissent attendre. Un badge bleu avec compte à rebours sera affiché."
          position="top"
        >
          <button
            onClick={() => setShowBeBack(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '0.75rem', borderRadius: 'var(--md-radius-lg)',
              border: `2px solid ${isBeBack ? '#3b82f6' : 'var(--md-outline)'}`,
              background: isBeBack ? 'rgba(59,130,246,0.10)' : 'var(--md-surface-2)',
              color: isBeBack ? '#2563eb' : 'var(--md-text-primary)',
              fontWeight: 700, fontSize: '0.88rem',
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'all 0.2s', width: '100%',
            }}
          >
            <Timer size={16} color={isBeBack ? '#2563eb' : undefined} />
            {isBeBack ? 'Modifier mon heure de retour' : 'Je reviens dans…'}
          </button>
        </Tooltip>
      )}

      <style>{`
        @keyframes statusPulse {
          0%   { box-shadow: 0 0 0 0 ${cfg.shadowColor}; }
          60%  { box-shadow: 0 0 0 8px rgba(0,0,0,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
        }
      `}</style>
    </div>
  );
};

export default StatusToggle;